// TEMPORARY page for checking live data without touching the database
// directly. Every check is a fixed, read-only query defined on the backend
// (backend/models/DbChecksModel.js); this page only picks one by id.
// Remove this folder, api/dbChecks.js and the /db-checks route when done.
import React, { useState } from "react"
import { useSelector } from "react-redux"
import { Button, Card, CardBody, Table } from "reactstrap"
import dbChecksApi from "../../../api/dbChecks"

const CHECK_GROUPS = [
  {
    title: "Money reconciliation",
    checks: [
      {
        id: "multi_stall_bookings",
        title: "Paid bookings with more than one stall",
        help: "The payment bugs only affect bookings with 2+ stalls. No rows means they have never hit live data.",
      },
      {
        id: "overwritten_stall_prices",
        title: "Stall prices different from the bazaar setup",
        help: "Evidence of the first stall's price being copied onto every stall of a booking. No rows means it never happened.",
      },
      {
        id: "invoice_vs_collected",
        title: "Invoice total vs money recorded (Rs 1+ difference)",
        help: "Every active invoice where the amount recorded against the booking differs from the invoice. Very old payments without payment details show collected = 0; ignore those.",
      },
    ],
  },
  {
    title: "Reports",
    checks: [
      {
        id: "regenerated_invoices",
        title: "Bookings with a cancelled + regenerated invoice",
        help: "For these, View Booking List shows Paid as shown_as_paid (about double) and a negative Due.",
      },
      {
        id: "balance_report_duplicates",
        title: "Payments the Balance Report shows more than once",
        help: "Look for the transaction ID in the Balance Report: it appears times_shown times.",
      },
    ],
  },
  {
    title: "Data already affected",
    checks: [
      {
        id: "paid_bookings_without_products",
        title: "Paid bookings with no products",
        help: "Sellers here who did choose products likely lost them through Update Data / Cancel & Regenerate.",
      },
      {
        id: "blank_booking_entries",
        title: "Bookings that show blank in the booking list",
        help: "The 'missing' column says why: no stall rows, seller details, stall from another bazaar, or blank stall number.",
      },
    ],
  },
]

const formatCell = value => {
  if (value === null || value === undefined) return <span className="text-muted">NULL</span>
  if (value === "") return <span className="text-muted">(empty)</span>
  return String(value)
}

const CheckResult = ({ result }) => {
  if (result.loading) return <div className="text-muted mt-2">Running…</div>
  if (result.error) return <div className="text-danger mt-2">{result.error}</div>

  const { rows, truncated, maxRows } = result.data
  if (rows.length === 0) {
    return <div className="text-success mt-2 fw-bold">No rows</div>
  }

  const columns = Object.keys(rows[0])
  return (
    <div className="mt-2">
      <div className="mb-1 fw-bold">
        {rows.length} row{rows.length === 1 ? "" : "s"}
        {truncated && ` (showing the first ${maxRows}; there are more)`}
      </div>
      <div style={{ maxHeight: 420, overflow: "auto" }}>
        <Table bordered size="sm" className="mb-0">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col} style={{ whiteSpace: "nowrap" }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                {columns.map(col => (
                  <td key={col}>{formatCell(row[col])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  )
}

const DbChecks = () => {
  document.title = "DB Checks (temporary) | PULA"
  const token = useSelector(state => state.auth.token)
  const [results, setResults] = useState({})

  const runCheck = async checkId => {
    setResults(prev => ({ ...prev, [checkId]: { loading: true } }))
    try {
      const response = await dbChecksApi.runCheck(checkId, token)
      if (response.status === 200 && response.data?.success) {
        setResults(prev => ({ ...prev, [checkId]: { data: response.data } }))
      } else {
        setResults(prev => ({
          ...prev,
          [checkId]: {
            error:
              response.data?.message ||
              `Check failed (HTTP ${response.status})`,
          },
        }))
      }
    } catch (error) {
      setResults(prev => ({
        ...prev,
        [checkId]: { error: error?.message || "Check failed" },
      }))
    }
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <h4 className="nom-title h4 mb-1">DB Checks (temporary)</h4>
        <p className="text-muted mb-3">
          Read-only checks against the live data. Nothing on this page can
          change the database. Admin login only.
        </p>

        {CHECK_GROUPS.map(group => (
          <div key={group.title} className="mb-4">
            <h5 className="mb-2">{group.title}</h5>
            {group.checks.map(check => (
              <Card key={check.id} className="mb-3">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start gap-3">
                    <div>
                      <div className="fw-bold">{check.title}</div>
                      <div className="text-muted small">{check.help}</div>
                    </div>
                    <Button
                      className="btn primary-button"
                      disabled={results[check.id]?.loading}
                      onClick={() => runCheck(check.id)}
                    >
                      Run
                    </Button>
                  </div>
                  {results[check.id] && <CheckResult result={results[check.id]} />}
                </CardBody>
              </Card>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export default DbChecks
